import React, { useState, useCallback, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

export type DropdownMenuItem = {
  label: string;
  icon?: React.ReactNode;
  onClick: () => void;
  danger?: boolean;
  disabled?: boolean;
  divider?: boolean;
};

export interface DropdownMenuProps {
  trigger: React.ReactNode;
  items: DropdownMenuItem[];
  align?: 'left' | 'right';
  className?: string;
}

const DropdownMenu = React.forwardRef<HTMLDivElement, DropdownMenuProps>(
  ({ trigger, items, align = 'left', className = '' }, ref) => {
    const [isOpen, setIsOpen] = useState(false);
    const [menuStyle, setMenuStyle] = useState<React.CSSProperties>({});
    const wrapperRef = useRef<HTMLDivElement>(null);
    const menuRef = useRef<HTMLDivElement>(null);

    const close = useCallback(() => setIsOpen(false), []);

    const updatePosition = useCallback(() => {
      if (!wrapperRef.current) return;
      const rect = wrapperRef.current.getBoundingClientRect();
      const menuWidth = 180;
      const style: React.CSSProperties = {
        position: 'fixed',
        top: rect.bottom + 4,
        minWidth: menuWidth,
      };
      if (align === 'right') {
        style.right = window.innerWidth - rect.right;
      } else {
        style.left = rect.left;
      }
      setMenuStyle(style);
    }, [align]);

    useEffect(() => {
      if (!isOpen) return;

      updatePosition();

      const handleClickOutside = (e: MouseEvent) => {
        const target = e.target as Node;
        if (
          wrapperRef.current && !wrapperRef.current.contains(target) &&
          menuRef.current && !menuRef.current.contains(target)
        ) {
          close();
        }
      };

      const handleEscape = (e: KeyboardEvent) => {
        if (e.key === 'Escape') close();
      };

      const handleScrollOrResize = () => {
        updatePosition();
      };

      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscape);
      window.addEventListener('scroll', handleScrollOrResize, true);
      window.addEventListener('resize', handleScrollOrResize);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        document.removeEventListener('keydown', handleEscape);
        window.removeEventListener('scroll', handleScrollOrResize, true);
        window.removeEventListener('resize', handleScrollOrResize);
      };
    }, [isOpen, close, updatePosition]);

    const handleTriggerClick = () => {
      if (!isOpen) updatePosition();
      setIsOpen((prev) => !prev);
    };

    const menu = isOpen ? (
      <div
        ref={menuRef}
        style={menuStyle}
        className="z-dropdown py-1 min-w-[180px] rounded-xl bg-surface-page border border-default shadow-xl backdrop-blur-xl animate-scale-in"
        role="menu"
      >
        {items.map((item, idx) => {
          if (item.divider) {
            return (
              <div key={idx} className="my-1 h-px bg-[var(--border-subtle)]" role="separator" />
            );
          }
          const { label, icon, onClick: onItemClick, danger, disabled } = item;
          return (
            <button
              key={idx}
              type="button"
              role="menuitem"
              disabled={disabled}
              onClick={() => {
                if (!disabled) {
                  onItemClick();
                  close();
                }
              }}
              className={`w-full px-3 py-2 text-sm text-content hover:bg-surface-secondary flex items-center gap-2.5 transition-colors cursor-pointer text-left ${
                danger ? '!text-status-error hover:bg-status-error/10' : ''
              } ${disabled ? 'opacity-40 cursor-not-allowed pointer-events-none' : ''}`}
            >
              {icon && <span className="shrink-0 [&>svg]:size-4">{icon}</span>}
              {label}
            </button>
          );
        })}
      </div>
    ) : null;

    return (
      <div
        ref={(node) => {
          (wrapperRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
          if (typeof ref === 'function') ref(node);
          else if (ref) ref.current = node;
        }}
        className={`relative inline-block ${className}`}
      >
        <div role="button" tabIndex={0} onClick={handleTriggerClick} onKeyDown={(e) => e.key === 'Enter' && handleTriggerClick()}>
          {trigger}
        </div>
        {createPortal(menu, document.body)}
      </div>
    );
  },
);

DropdownMenu.displayName = 'DropdownMenu';

export default DropdownMenu;
